<style lang="scss" scoped>
.day-configurator-side-panel {
    width: 100vw;
    height: 100vh;
    position: fixed;
    pointer-events: none;
    cursor: pointer;
    top: 0;
    left: 0;
    z-index: 10000;
    overflow: scroll;
    -ms-overflow-style: none;

    &.active {
        pointer-events: all;
        backdrop-filter: saturate(100%) blur(10px);

        .slideout {
            right: 0;
            opacity: 1;
        }
    }

    .slideout {
        position: fixed;
        width: 960px;
        right: -980px;
        cursor: initial;
        height: 100%;
        opacity: 0;
        top: 0;
        background: #fff;
        box-shadow: 0 0 32px 0 rgba(0, 0, 0, 0.3);
        transition: right 0.15s ease-in-out, opacity 0.15s ease-in-out;
        overflow-y: auto;
        overflow-x: hidden;

        @media screen and (max-width: 960px) {
            width: 100%;
            right: -100%;
        }

        .close {
            position: absolute;
            width: 15px;
            height: 15px;
            right: 56px;
            top: 67px;
            cursor: pointer;

            @media screen and (max-width: 767.98px) {
                right: 16px;
                top: 26px;
            }

            &:before,
            &:after {
                content: "";
                background: #cccccc;
                position: absolute;
                width: 15px;
                height: 3px;
                border-radius: 2px;
                left: 0;
                transform-origin: left;
                transition: background 0.15s ease-in-out;
            }

            &:before {
                top: 1px;
                transform: rotate(45deg);
            }

            &:after {
                bottom: 0;
                transform: rotate(-45deg);
            }

            &:hover {
                &:before,
                &:after {
                    background: var(--dark);
                }
            }
        }

        h4 {
            font-size: 1.5rem;
            font-weight: 600;
            line-height: 2.125rem;
            margin-bottom: 0;
        }

        p {
            font-size: 1rem;
        }

        a {
            color: #f39225;
            font-size: 1.25rem;
            font-weight: 200;
            text-decoration: underline;

            @media screen and (max-width: 767.98px) {
                font-size: 0.875rem;
            }
        }

        .label {
            color: #8a8a8a;
            font-size: 1.25rem;
            font-weight: 200;
            display: inline-block;
            width: 133px;

            @media screen and (max-width: 767.98px) {
                display: block;
                width: auto;
                font-size: 0.875rem;
            }
        }

        .value {
            font-size: 1.25rem;
            font-weight: 200;
        }
    }
}
::v-deep .btn .arrow-right {
    margin-left: 16px;
    transition: transform 0.15s ease-in-out;
}
</style>

<template>
    <div class="day-configurator-side-panel" @click="close">
        <div class="slideout px-16 py-24 p-md-56" @click.stop>
            <div class="close" @click="close"></div>
            <h3 class="mb-16 mb-md-40">
                {{ translations.request_offer_slideout.title }}
            </h3>
            <div class="row large-gutters mb-32 mb-md-56">
                <div class="col-12 col-lg-6 mb-32 mb-lg-0">
                    <div
                        class="d-flex justify-content-between align-items-center mb-8 mb-md-16"
                    >
                        <h4>
                            {{ translations.request_offer_slideout.summary }}
                        </h4>
                        <a @click.prevent="firstStep">{{
                            translations.request_offer_slideout.modify
                        }}</a>
                    </div>
                    <div class="d-flex flex-column flex-md-row mb-8 mb-md-16">
                        <span class="label">{{
                            translations.request_offer_slideout.date
                        }}</span>
                        <span class="value">{{ formattedDate }}</span>
                    </div>
                    <div class="d-flex flex-column flex-md-row mb-8 mb-md-16">
                        <span class="label">{{
                            translations.request_offer_slideout.location
                        }}</span>
                        <span class="value">{{ formattedLocation }}</span>
                    </div>
                    <div class="d-flex flex-column flex-md-row">
                        <span class="label">{{
                            translations.request_offer_slideout.attendees
                        }}</span>
                        <span class="value">{{ formattedAttendees }}</span>
                    </div>
                </div>
                <div class="col-12 col-lg-6">
                    <div
                        class="d-flex  justify-content-between align-items-center mb-8 mb-md-16"
                    >
                        <h4>
                            {{ translations.request_offer_slideout.schedule }}
                        </h4>
                        <a @click.prevent="$emit('previous-step')">{{
                            translations.request_offer_slideout.modify
                        }}</a>
                    </div>
                    <div
                        class="d-flex flex-column flex-md-row mb-8 mb-md-16"
                        v-for="(activity, index) in sortedActivities"
                        :key="`${index}${activity.start}`"
                    >
                        <span class="label">{{
                            activityStartEndString(activity)
                        }}</span>
                        <span
                            class="value"
                            v-html="activityNameString(activity)"
                        ></span>
                    </div>
                </div>
            </div>

            <h4 class="mb-16">
                {{ translations.request_offer_slideout.contact_info }}
            </h4>

            <ValidationObserver
                v-slot="{ invalid }"
                tag="div"
                :slim="true"
                ref="observer"
            >
                <div class="row large-gutters mb-32">
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.first_name"
                            class="mb-24"
                            type="text"
                            :label="
                                translations.request_offer_slideout.first_name
                            "
                            rules="required"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.last_name"
                            class="mb-24"
                            type="text"
                            :label="
                                translations.request_offer_slideout.last_name
                            "
                            rules="required"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.email"
                            class="mb-24"
                            type="email"
                            :label="translations.request_offer_slideout.email"
                            rules="required|email"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.phone"
                            class="mb-24"
                            type="text"
                            :label="translations.request_offer_slideout.phone"
                            rules="required"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.vat"
                            class="mb-24"
                            type="text"
                            :label="
                                translations.request_offer_slideout.vat_number
                            "
                            rules="required"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <el-input
                            v-model="formData.company"
                            class="mb-24"
                            type="text"
                            :label="translations.request_offer_slideout.company"
                            rules="required"
                        />
                    </div>
                </div>

                <h4 class="mb-8">
                    {{ translations.request_offer_slideout.remarks }}
                </h4>
                <p class="mb-24 font-weight-light">
                    {{
                        translations.request_offer_slideout.remarks_description
                    }}
                </p>

                <el-input
                    v-model="formData.remarks"
                    type="textarea"
                    :label="translations.request_offer_slideout.message"
                    rules=""
                />

                <hr class="mx-n56 mt-56 mb-24" />

                <el-button
                    :class="'btn btn-accent text-white'"
                    @click="submit"
                    :loading="loading"
                    :success="success"
                    :loadingLabel="translations.request_offer_slideout.button"
                    :successLabel="translations.request_offer_slideout.button"
                    :defaultLabel="translations.request_offer_slideout.button"
                    :disabled="invalid || success"
                ></el-button>
            </ValidationObserver>
        </div>
        <success-popover
            :class="{ active: success }"
            :title="translations.request_offer_slideout.success_title"
            :description="
                translations.request_offer_slideout.success_description
            "
            :cta="translations.request_offer_slideout.success_cta"
        ></success-popover>
    </div>
</template>

<script>
var cloneDeep = require("lodash.clonedeep");

import { bus } from "@/app";
import core from "./day-configurator-core";
import hasForm from "../mixins/has-form";
import { ValidationObserver, ValidationProvider, validate } from "vee-validate";
import { extend } from "vee-validate";
import * as rules from "vee-validate/dist/rules";
import nl from "vee-validate/dist/locale/nl.json";
for (let rule in rules) {
    extend(rule, {
        ...rules[rule],
        message: nl.messages[rule]
    });
}

export default {
    mixins: [core, hasForm],

    components: { ValidationObserver, ValidationProvider },

    data: () => ({
        address: "",
        formData: {
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            vat: "",
            company: "",
            remarks: ""
        },
        sortedActivities: []
    }),

    created() {
        let self = this;
        bus.$on("addressUpdate", address => {
            self.address = address;
        });
        bus.$on("changeStep", step => {
            if (step === "request-offer") {
                self.getSortedActivities();
            }
        });
    },

    methods: {
        close() {
            bus.$emit("changeStep", "planning");
        },

        submit() {
            this.beforeSubmit();
            axios
                .post(`/api/schedules/${this.schedule.id}/offer`, this.formData)
                .then(response => {
                    this.handleSuccess();
                })
                .catch(e => this.handleError(e));
        },

        getSortedActivities() {
            if (
                this.schedule.activities &&
                this.schedule.activities.length > 0
            ) {
                let today = new Date();
                let sortedActivities = cloneDeep(this.schedule.activities).sort(
                    function(a, b) {
                        let aArray = a.start.split(":");
                        let bArray = b.start.split(":");

                        let aDate = new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            today.getDate(),
                            aArray[0],
                            aArray[1]
                        );
                        let bDate = new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            today.getDate(),
                            bArray[0],
                            bArray[1]
                        );

                        return aDate - bDate;
                    }
                );
                this.sortedActivities = sortedActivities;
            }
        },

        activityNameString(activity) {
            switch (activity.type) {
                case "listed":
                    let externalItem = this.getExternalItem(activity);
                    return externalItem
                        ? externalItem.post_title
                            ? externalItem.post_title
                            : externalItem.title.rendered
                        : "";
                case "own":
                    return activity.data.name;
                case "catering":
                    return this.translations.day_planning[activity.data.type]
                        .label;
                case "break":
                    return this.translations.day_planning.break.label;
            }
            return "";
        },

        firstStep() {
            bus.$emit("changeStep", "practical-details");
        }
    },

    computed: {
        formattedLocation() {
            let radiusString = "";
            if (this.schedule.radius != null && this.schedule.radius != "")
                radiusString = `${this.schedule.radius} km rondom `;
            return `${radiusString}${this.address}`;
        }
    }
};
</script>
